/* 💎 Dragonious Gem Atlas — canon catalog of the 7 gems + matching shards
   (Feb 2026). Read-only registry page. More gems & recipes TBD per owner. */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GemAtlasPage() {
  const [gems, setGems] = useState([]);
  const [pickedShard, setPickedShard] = useState(null);   // 🔍 Shard Inspector state
  const [meta, setMeta] = useState({ discovered_count: 0, total: 7, auth_aware: false });
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/gems/canon`);
        setGems(r.data?.gems || []);
        setMeta({
          discovered_count: r.data?.discovered_count ?? null,
          total:            r.data?.total ?? 7,
          auth_aware:       !!r.data?.auth_aware,
        });
      } catch (e) { /* silent */ }
    })();
  }, []);

  // Only allow inspecting shards of DISCOVERED gems (when auth-aware)
  const inspectableGems = meta.auth_aware ? gems.filter(g => g.discovered) : gems;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0A0A] via-[#101018] to-black text-white">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <button
          data-testid="gem-atlas-back"
          onClick={() => navigate(-1)}
          className="text-xs text-neutral-500 hover:text-neutral-300 mb-3">
          ← Back
        </button>
        <h1 className="text-3xl sm:text-4xl font-black tracking-wide mb-1" style={{ fontFamily: 'Anton, sans-serif' }}>
          💎 <span className="text-amber-300">DRAGONIOUS GEM ATLAS</span>
        </h1>
        <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
          <p className="text-xs text-neutral-400 max-w-2xl">
            The seven canon gems of Dragonious. Each gem maps to a single hero stat
            and refines from its matching Shard ingredient. <span className="italic text-neutral-500">(More gems & recipes TBD)</span>
          </p>
          {meta.auth_aware && (
            <div className="px-3 py-2 rounded-lg border border-amber-600/40 bg-amber-900/20 text-right"
                 data-testid="gem-discovery-counter">
              <div className="text-[10px] uppercase tracking-wider text-amber-400">Discovery</div>
              <div className="text-lg font-black text-amber-200">
                {meta.discovered_count} <span className="text-amber-500/70 text-xs">/ {meta.total}</span>
              </div>
              <div className="text-[9px] text-neutral-500 italic">
                {meta.discovered_count === meta.total ? "Atlas complete!" : "Refine shards to discover."}
              </div>
            </div>
          )}
        </div>

        {/* 🔍 Shard Inspector — click a shard, see which gem it refines into.
            When auth-aware, only DISCOVERED gems' shards are shown. */}
        {inspectableGems.length > 0 && (
          <div className="mb-6 rounded-xl border border-emerald-700/40 bg-gradient-to-br from-emerald-950/30 via-black to-black p-4"
               data-testid="shard-inspector">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-emerald-300/70">🔍 Shard Inspector</div>
                <div className="text-[10px] text-neutral-500 italic mt-0.5">
                  {meta.auth_aware
                    ? "Tap a shard to see which gem it refines into. Locked gems hide their shards."
                    : "Tap a shard to see which gem it refines into."}
                </div>
              </div>
              {pickedShard && (
                <button
                  data-testid="shard-inspector-clear"
                  onClick={() => setPickedShard(null)}
                  className="text-[10px] px-2 py-1 rounded bg-neutral-800 hover:bg-neutral-700 text-neutral-400">
                  ✕ Clear
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {inspectableGems.map((g) => {
                const isPicked = pickedShard === g.shard_id;
                return (
                  <button
                    key={`shard-chip-${g.shard_id}`}
                    data-testid={`shard-chip-${g.shard_id}`}
                    onClick={() => setPickedShard(isPicked ? null : g.shard_id)}
                    className={`px-3 py-2 rounded-lg border-2 text-xs font-bold transition-all ${
                      isPicked
                        ? "scale-105 shadow-lg"
                        : "border-neutral-700 hover:border-neutral-500 opacity-80 hover:opacity-100"
                    }`}
                    style={isPicked ? {
                      borderColor: g.color_hex,
                      backgroundColor: g.color_hex + "20",
                      boxShadow: `0 0 16px ${g.color_hex}60`,
                    } : {}}>
                    <span className="text-base mr-1">{g.shard_icon}</span>
                    {g.shard_label}
                  </button>
                );
              })}
            </div>
            {pickedShard && (() => {
              const g = inspectableGems.find(x => x.shard_id === pickedShard);
              if (!g) return null;
              return (
                <div className="px-4 py-3 rounded-lg border-2 bg-black/40"
                     style={{ borderColor: g.color_hex + "80", boxShadow: `inset 0 0 24px ${g.color_hex}25` }}
                     data-testid="shard-inspector-result">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="text-3xl">{g.shard_icon}</div>
                    <div className="text-xl text-neutral-500">→</div>
                    <div className="text-4xl drop-shadow-lg"
                         style={{ filter: `drop-shadow(0 0 10px ${g.color_hex}90)` }}>
                      {g.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-lg" style={{ color: g.color_hex }}>
                        {g.name}
                      </div>
                      <div className="text-xs text-neutral-400">
                        Refines into a <span style={{ color: g.color_hex }}>{g.name}</span> gem · boosts <span className="text-amber-200 font-bold">{g.stat_label}</span>
                      </div>
                      <div className="text-[10px] text-neutral-500 italic mt-1">{g.blurb}</div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="gem-atlas-list">
          {gems.map((g) => {
            const isHighlighted = pickedShard === g.shard_id;
            const isLocked = meta.auth_aware && g.discovered === false;
            return (
            <div key={g.id}
                 data-testid={`gem-${g.id}`}
                 className={`rounded-xl border-2 p-4 bg-gradient-to-br from-black/40 to-neutral-950 transition-all ${
                   isHighlighted ? "scale-[1.02]" : ""
                 } ${isLocked ? "opacity-60 grayscale" : ""}`}
                 style={{
                   borderColor: g.color_hex + (isHighlighted ? "FF" : "60"),
                   boxShadow: isHighlighted ? `0 0 24px ${g.color_hex}50` : "none",
                 }}>
              {isLocked && (
                <div className="absolute -mt-2 -ml-2 text-base" title="Locked — refine the matching shard to discover">
                  🔒
                </div>
              )}
              <div className="flex items-start gap-3">
                <div className="text-4xl drop-shadow-lg"
                     style={{ filter: `drop-shadow(0 0 8px ${g.color_hex}90)` }}>
                  {g.icon}
                </div>
                <div className="flex-1">
                  <div className="font-black text-lg tracking-wide" style={{ color: g.color_hex }}>
                    {g.name}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500">
                    {g.color.replace(/_/g, " / ")} gem
                  </div>
                </div>
              </div>

              <div className="mt-3 px-3 py-2 rounded-lg bg-black/40 border border-amber-900/30">
                <div className="text-[10px] uppercase tracking-wider text-amber-400">Boosts</div>
                <div className="text-amber-200 font-bold">{g.stat_label}</div>
              </div>

              <div className="mt-2 px-3 py-2 rounded-lg bg-black/40 border border-emerald-900/30">
                <div className="text-[10px] uppercase tracking-wider text-emerald-400">Refined from</div>
                <div className="text-emerald-200 text-sm">
                  {g.shard_icon} {g.shard_label}
                </div>
              </div>

              <div className="mt-3 text-[10px] text-neutral-400 italic leading-relaxed">
                {g.blurb}
              </div>
            </div>
            );
          })}
        </div>

        {/* Stat-key legend */}
        <div className="mt-6 rounded-xl border border-neutral-800 bg-black/30 p-4">
          <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">📖 Quick Map</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-[11px]">
            {gems.map((g) => (
              <div key={`map-${g.id}`} className="flex items-center gap-1.5">
                <span style={{ color: g.color_hex }}>{g.icon}</span>
                <span className="font-bold">{g.name}</span>
                <span className="text-neutral-500">→</span>
                <span className="text-amber-200">{g.stat.toUpperCase()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
